import React from 'react';
import withLayout from 'hocs/withLayout';
import { useQuery } from '@apollo/client/react';
import PostType, { PostsByTypeQueryVariables } from './PostType';
import { PostType as PostTypeEnum, PostCardInfo } from 'types/post';
import { POSTS_BY_TYPE } from 'lib/graphql/queries/post';
import { Seo } from '@elevator/ui';
import { PageProps } from 'types/page';
import { DOMAIN_BASE_SHORT, ROUTES } from 'config/Nav';

/**
 * PostType Layout Wrapper
 *
 * Handles client-side data fetching for post type pages (Submitted, Sponsored, Premiere).
 * Uses cache-first fetchPolicy to avoid duplicate fetches and improve performance.
 *
 * Features:
 * - Cache-first fetchPolicy (avoids duplicate fetches)
 * - Loading state management
 * - Error handling
 * - SEO metadata
 *
 * Used in:
 * - /type/submissions
 * - /type/sponsored
 * - /type/premiered
 * - /type/promoted
 *
 * @param props - Layout props
 * @param props.type - Post type enum (Submitted, Sponsored, Premiere, Promoted)
 * @param props.sectionTitle - Optional section title override
 * @returns PostType layout with data management
 */
const Index = ({ type, sectionTitle }: PostTypeIndexPropTypes) => {
  const variables: PostsByTypeQueryVariables = {
    type,
    limit: 9,
    skip: 0,
  };

  const { data, loading, error, networkStatus, fetchMore } = useQuery<{
    publishedPosts: PostCardInfo[];
  }>(POSTS_BY_TYPE, {
    fetchPolicy: 'cache-first', // Use cache-first to avoid unnecessary re-fetch
    nextFetchPolicy: 'cache-first', // Keep using cache for subsequent fetches
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const posts = data?.publishedPosts || [];
  const isLoading = (loading && networkStatus !== 3) || networkStatus === 4;
  const loadingMore = networkStatus === 3;

  return (
    <>
      <Seo
        type={'website'}
        data={{
          baseUrl: DOMAIN_BASE_SHORT,
          title: type,
          path: ROUTES.TYPE.to + type,
        }}
      />
      <PostType
        type={type}
        posts={posts}
        loading={isLoading}
        loadingMore={loadingMore}
        error={error}
        fetchMore={fetchMore}
        variables={variables}
        sectionTitle={sectionTitle}
      />
    </>
  );
};

export type PostTypeIndexPropTypes = PageProps & {
  type: PostTypeEnum;
  sectionTitle?: string;
};

export default withLayout(Index, { bottomAd: true, margin: true });
